import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminReservationTable from "./AdminReservationTable";
import { ADMIN_STORAGE_KEY } from "./AdminLogin";
import Popup from "../ui/Popup";
import SlotGrid from "../reservations/SlotGrid";
import { BrandMark } from "../art/Icons";
import { BRAND_NAME } from "../../config/event";
import { ACTIVITIES, MAIN_ACTIVITY, getActivityByTable } from "../../config/activities";

const RESERVATIONS_ENDPOINT = "/backend/admin_reservations.php";
const LOGOUT_ENDPOINT = "/backend/admin_logout.php";
const JSON_HEADERS = { "Content-Type": "application/json" };
const INITIAL_FILTERS = { timeSlot: "", search: "" };

// The console shows the first two activities side by side (as it always has).
const DASHBOARD_TABLES = [
  { key: "table1", title: "Table 1 · Stezka", icon: "☠", matches: (r) => r.table_name !== "reservations_mira" },
  { key: "table2", title: "Table 2 · Ofrenda", icon: "🕯", matches: (r) => r.table_name === "reservations_mira" },
];

const activityFor = (reservation) => getActivityByTable(reservation.table_name) ?? MAIN_ACTIVITY;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [timeChangeTarget, setTimeChangeTarget] = useState(null);
  const [availabilityByTable, setAvailabilityByTable] = useState({});

  const fetchReservations = useCallback(
    async (activeFilters) => {
      try {
        const params = new URLSearchParams();
        if (activeFilters.timeSlot) params.append("time_slot", activeFilters.timeSlot);
        if (activeFilters.search) params.append("search", activeFilters.search);
        const response = await fetch(`${RESERVATIONS_ENDPOINT}?${params}`);
        if (response.status === 401) {
          localStorage.removeItem(ADMIN_STORAGE_KEY);
          navigate("/admin");
          return;
        }
        const data = await response.json();
        if (!response.ok || !data.reservations) {
          setError(data.error || `HTTP ${response.status}: Failed to fetch reservations`);
          return;
        }
        setReservations(data.reservations);
        setStats(data.stats || {});
        setError("");
      } catch (err) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  const fetchAvailability = useCallback(async () => {
    try {
      const responses = await Promise.all(ACTIVITIES.map((activity) => fetch(activity.api.availability)));
      if (responses.some((response) => !response.ok)) return;
      const payloads = await Promise.all(responses.map((response) => response.json()));
      setAvailabilityByTable(
        Object.fromEntries(
          ACTIVITIES.map((activity, index) => [
            activity.table,
            { availability: payloads[index].availability || {}, capacityInfo: payloads[index].capacity_info || {} },
          ])
        )
      );
    } catch (err) {
      console.error("Error fetching time availability:", err);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_STORAGE_KEY)) {
      navigate("/admin");
      return;
    }
    fetchReservations(INITIAL_FILTERS);
    fetchAvailability();
  }, [navigate, fetchReservations, fetchAvailability]);

  const handleFilterChange = (event) => {
    setFilters((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleDelete = async (reservation) => {
    try {
      const response = await fetch(activityFor(reservation).api.cancel, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ reservationId: reservation.id }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Failed to delete reservation");
        return;
      }
      await fetchReservations(filters);
      setDeleteTarget(null);
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };

  const handleTimeChange = async (reservation, newTimeSlot) => {
    try {
      const response = await fetch(activityFor(reservation).api.timeChange, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ reservationId: reservation.id, newTimeSlot }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || "Failed to change time slot");
        return;
      }
      await fetchReservations(filters);
      await fetchAvailability();
      setTimeChangeTarget(null);
      setError("");
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(LOGOUT_ENDPOINT, { method: "POST", headers: JSON_HEADERS });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem(ADMIN_STORAGE_KEY);
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="centered-screen">
        <p className="msg info">
          <span className="spinner" />
          Loading dashboard…
        </p>
      </div>
    );
  }

  const targetActivity = timeChangeTarget ? activityFor(timeChangeTarget) : null;
  const targetAvailability = targetActivity ? availabilityByTable[targetActivity.table] ?? {} : {};

  return (
    <>
      <header className="site-header">
        <div className="nav">
          <Link className="brand" to="/">
            <BrandMark />
            {BRAND_NAME} · Admin
          </Link>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </header>

      <main className="page pb-16">
        <div className="rhead">
          <span className="kicker">Reservation management console</span>
          <h1 className="display-h1">ADMIN DASHBOARD</h1>
        </div>

        <div className="stats">
          <div className="stat">
            <div className="lbl">Total reservations</div>
            <div className="val">{stats.total_reservations || 0}</div>
          </div>
          {DASHBOARD_TABLES.map((table) => (
            <div className="stat" key={table.key}>
              <div className="lbl">{table.title}</div>
              <div className="val">{reservations.filter(table.matches).length}</div>
            </div>
          ))}
          <div className="stat">
            <div className="lbl">Time slots</div>
            <div className="val">{stats.unique_time_slots || 0}</div>
          </div>
        </div>

        <section className="card mb-6">
          <h3>Filters</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="field">
              <label htmlFor="filter-slot">Time slot</label>
              <select id="filter-slot" name="timeSlot" value={filters.timeSlot} onChange={handleFilterChange}>
                <option value="">All time slots</option>
                {MAIN_ACTIVITY.slots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="filter-search">Search</label>
              <input
                id="filter-search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Team name…"
              />
            </div>
          </div>
          <div>
            <button type="button" className="btn btn-marigold btn-sm" onClick={() => fetchReservations(filters)}>
              Apply filters
            </button>
          </div>
        </section>

        {error && (
          <p className="msg error mb-6" role="alert">
            {error}
          </p>
        )}
        <p className="msg info mb-6">Tip: click a reservation's time to move it to another slot.</p>

        <div className="grid gap-6 lg:grid-cols-2">
          {DASHBOARD_TABLES.map((table) => (
            <AdminReservationTable
              key={table.key}
              title={table.title}
              icon={table.icon}
              rows={reservations.filter(table.matches)}
              onChangeTime={setTimeChangeTarget}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      </main>

      <Popup isOpen={Boolean(timeChangeTarget)} onClose={() => setTimeChangeTarget(null)} title="Change time slot" wide>
        {timeChangeTarget && (
          <>
            <p>
              Select a new time slot for <b className="text-marigold">{timeChangeTarget.team_name}</b> (
              {targetActivity.shortName}).
            </p>
            <SlotGrid
              slots={targetActivity.slots}
              availability={targetAvailability.availability}
              capacityInfo={targetAvailability.capacityInfo}
              onChange={(slot) => handleTimeChange(timeChangeTarget, slot)}
              teamSize={Number(timeChangeTarget.team_number) || 1}
              currentSlot={timeChangeTarget.time_slot}
              wide
            />
          </>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setTimeChangeTarget(null)}>
            Cancel
          </button>
        </div>
      </Popup>

      <Popup isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete reservation">
        <p>
          Are you sure you want to delete the reservation for <b className="text-marigold">{deleteTarget?.team_name}</b>?
          This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => handleDelete(deleteTarget)}>
            Delete
          </button>
        </div>
      </Popup>
    </>
  );
};

export default AdminDashboard;
